import { forwardRef } from 'react';
import type { Requisition } from '../../types';

interface PrintablePickListProps {readonly requisition: Requisition;}

export const PrintablePickList = forwardRef<HTMLDivElement, PrintablePickListProps>(
  function PrintablePickList({ requisition }, ref) {
    const neededByDate = new Date(requisition.neededBy);

    return (
      <div ref={ref} className="bg-white p-8 print:p-4">
        {/* Header */}
        <div className="mb-6 border-b-2 border-black pb-4">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold">PICK LIST</h1>
              <p className="text-lg font-medium">{requisition.requisitionId}</p>
            </div>
            <div className="text-right">
              <p className="text-sm">
                <span className="font-medium">Date:</span> {new Date().toLocaleDateString()}
              </p>
              <p className="text-sm">
                <span className="font-medium">Need By:</span> {neededByDate.toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Venue Info */}
        <div className="mb-6 grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-medium">Deliver To:</p>
            <p className="text-lg">{requisition.venueName}</p>
          </div>
          <div>
            <p className="font-medium">Requested By:</p>
            <p>{requisition.requestedByName}</p>
          </div>
        </div>

        {/* Items Table */}
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b-2 border-black">
              <th className="w-12 py-2 text-left">✓</th>
              <th className="py-2 text-left">Item</th>
              <th className="py-2 text-left">SKU</th>
              <th className="w-24 py-2 text-right">Qty</th>
              <th className="w-20 py-2 text-left">Unit</th>
              <th className="py-2 text-left">Notes</th>
            </tr>
          </thead>
          <tbody>
            {requisition.items.map((item, index) => (
              <tr key={item.lineId} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                <td className="border-b py-3">
                  <div className="h-5 w-5 border-2 border-black" />
                </td>
                <td className="border-b py-3 font-medium">{item.itemName}</td>
                <td className="border-b py-3 text-sm text-gray-600">{item.sku}</td>
                <td className="border-b py-3 text-right font-bold">
                  {item.approvedQuantity ?? item.requestedQuantity}
                </td>
                <td className="border-b py-3">{item.unit}</td>
                <td className="border-b py-3 text-sm text-gray-600">{item.notes ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Footer */}
        <div className="mt-8 border-t-2 border-black pt-4">
          <div className="grid grid-cols-2 gap-8">
            <div>
              <p className="mb-2 text-sm font-medium">Picked By:</p>
              <div className="h-8 border-b border-black" />
            </div>
            <div>
              <p className="mb-2 text-sm font-medium">Date/Time:</p>
              <div className="h-8 border-b border-black" />
            </div>
          </div>
          <div className="mt-4">
            <p className="mb-2 text-sm font-medium">Notes:</p>
            <div className="h-16 border border-black" />
          </div>
        </div>

        {/* Total Items */}
        <div className="mt-4 text-right text-sm">
          <p>
            <span className="font-medium">Total Items:</span> {requisition.totalItems}
          </p>
        </div>
      </div>
    );
  }
);
